// Reports API surface (spec #42 – #47).
import { fetchJson, fetchWithAuth } from './http';
import type { ReportJson, ReportListResponse, ReportPhotoJson, ReportStatus } from './types';

// Photo upload wire shapes (spec #48 – #52).
// These mirror the Backend Zod schemas in `backend/src/validators/photos.ts`.
// Kept here so the rest of the Web client never has to inline string
// literals for field names; the Backend is the contract authority and
// changes here must come with a corresponding Backend change.
export const ALLOWED_PHOTO_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
] as const;
export type AllowedPhotoMimeType = (typeof ALLOWED_PHOTO_MIME_TYPES)[number];

export const MAX_PHOTO_BYTES = 15 * 1024 * 1024; // 15 MiB (spec #51).

/**
 * Payload for `POST /reports/:id/photos/upload-url`. Mirrors
 * `photoUploadUrlSchema` in the Backend; do not add fields the
 * Backend has not accepted.
 */
export interface PhotoUploadUrlRequest {
  client_photo_id: string; // UUID v4
  filename: string; // 1..255 chars
  mime_type: AllowedPhotoMimeType;
  size_bytes: number; // 1..15 MiB
  checksum?: string; // optional, 8..128 chars
  thumbnail_bytes?: number; // optional, 1..256 KiB
}

/**
 * Parsed response shape from the upload-url endpoint. The Backend
 * wraps the payload in `{ data: ... }`; the unwrap happens inside
 * `requestUploadUrl` so callers see the raw result.
 */
export interface PhotoUploadUrlResult {
  photo_id: string;
  r2_key: string;
  upload_url: string;
  headers: Record<string, string>;
  expires_in: number;
  expires_at: string;
  thumbnail_r2_key: string | null;
  thumbnail_upload_url: string | null;
  thumbnail_expires_at: string | null;
  thumbnail_headers: Record<string, string> | null;
}

/**
 * Payload for `POST /reports/:id/photos/finalize`. Mirrors
 * `photoFinalizeSchema` in the Backend.
 */
export interface PhotoFinalizeRequest {
  photo_id: string;
  width?: number;
  height?: number;
  size_bytes?: number;
  checksum?: string;
}

export interface ListReportsArgs {
  status?: ReportStatus;
  since?: string;
  cursor?: string;
  limit?: number;
  search?: string;
}

export const reportsApi = {
  async list(
    getToken: () => string | null,
    args: ListReportsArgs = {},
  ): Promise<ReportListResponse> {
    const params = new URLSearchParams();
    if (args.status) params.set('status', args.status);
    if (args.since) params.set('since', args.since);
    if (args.cursor) params.set('cursor', args.cursor);
    if (args.limit) params.set('limit', String(args.limit));
    if (args.search) params.set('q', args.search);
    const query = params.toString();
    const rows = await fetchJson<ReportJson[]>(`/reports${query ? `?${query}` : ''}`, getToken);
    return { rows: rows ?? [], nextCursor: null };
  },

  async get(getToken: () => string | null, id: string) {
    return await fetchJson<ReportJson>(`/reports/${encodeURIComponent(id)}`, getToken);
  },

  async create(
    getToken: () => string | null,
    input: {
      client_report_id: string;
      title: string;
      content: string;
      client_updated_at?: string;
    },
  ) {
    return await fetchJson<ReportJson & { idempotent?: boolean }>('/reports', getToken, {
      method: 'POST',
      body: input,
    });
  },

  async update(
    getToken: () => string | null,
    id: string,
    input: { base_version: number; title?: string; content?: string; client_updated_at?: string },
  ) {
    return await fetchJson<ReportJson>(`/reports/${encodeURIComponent(id)}`, getToken, {
      method: 'PATCH',
      body: input,
    });
  },

  async submit(getToken: () => string | null, id: string) {
    return await fetchJson<ReportJson>(`/reports/${encodeURIComponent(id)}/submit`, getToken, {
      method: 'POST',
      body: {},
    });
  },

  async archive(getToken: () => string | null, id: string) {
    return await fetchJson<ReportJson>(`/reports/${encodeURIComponent(id)}/archive`, getToken, {
      method: 'POST',
      body: {},
    });
  },

  async restore(getToken: () => string | null, id: string) {
    return await fetchJson<ReportJson>(`/reports/${encodeURIComponent(id)}/restore`, getToken, {
      method: 'POST',
      body: {},
    });
  },

  async deleteReport(getToken: () => string | null, id: string) {
    await fetchJson(`/reports/${encodeURIComponent(id)}`, getToken, { method: 'DELETE' });
  },
};

export const photosApi = {
  async list(
    getToken: () => string | null,
    reportId: string,
    opts: { since?: string } = {},
  ): Promise<ReportPhotoJson[]> {
    const params = new URLSearchParams();
    if (opts.since) params.set('since', opts.since);
    const query = params.toString();
    const rows = await fetchJson<ReportPhotoJson[]>(
      `/reports/${encodeURIComponent(reportId)}/photos${query ? `?${query}` : ''}`,
      getToken,
    );
    return rows ?? [];
  },

  thumbnailUrl(reportId: string, photoId: string) {
    // The Worker exposes a streamed thumbnail proxy (spec #48–#52). The
    // web client piggybacks on the same auth-aware endpoint.
    return `/reports/${encodeURIComponent(reportId)}/photos/${encodeURIComponent(photoId)}/thumbnail`;
  },

  imageUrl(reportId: string, photoId: string) {
    return `/reports/${encodeURIComponent(reportId)}/photos/${encodeURIComponent(photoId)}/image`;
  },

  async deletePhoto(getToken: () => string | null, reportId: string, photoId: string) {
    await fetchJson(
      `/reports/${encodeURIComponent(reportId)}/photos/${encodeURIComponent(photoId)}`,
      getToken,
      { method: 'DELETE' },
    );
  },

  /**
   * Fetches a binary asset (thumbnail / image) using the user's bearer
   * token. Returns a blob URL the caller can put in `<img src=…>`.
   */
  async fetchAsset(getToken: () => string | null, path: string): Promise<string> {
    const res = await fetchWithAuth(path, getToken);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  /**
   * Request a presigned R2 PUT URL for the original photo + (when
   * `thumbnail_bytes` is set) the companion thumbnail key. Authenticated;
   * the Backend validates report ownership and the per-user photo quota.
   *
   * Idempotency: the Backend dedupes on `(report_id, client_photo_id)`
   * so retries from the same `clientPhotoId` always reuse the same
   * server-side `photo_id` + `r2_key`. The caller is responsible for
   * generating the UUID once per pick and reusing it across retries.
   */
  async requestUploadUrl(
    getToken: () => string | null,
    reportId: string,
    input: PhotoUploadUrlRequest,
  ): Promise<PhotoUploadUrlResult> {
    const data = await fetchJson<PhotoUploadUrlResult>(
      `/reports/${encodeURIComponent(reportId)}/photos/upload-url`,
      getToken,
      {
        method: 'POST',
        body: input,
      },
    );
    return {
      photo_id: data.photo_id,
      r2_key: data.r2_key,
      upload_url: data.upload_url,
      headers: data.headers ?? {},
      expires_in: data.expires_in ?? 900,
      expires_at: data.expires_at,
      thumbnail_r2_key: data.thumbnail_r2_key ?? null,
      thumbnail_upload_url: data.thumbnail_upload_url ?? null,
      thumbnail_expires_at: data.thumbnail_expires_at ?? null,
      thumbnail_headers: data.thumbnail_headers ?? null,
    };
  },

  /**
   * Mark a photo as uploaded after the R2 PUTs have landed. Authenticated;
   * the Backend re-checks R2 HEAD for `r2_key` (and `thumbnail_r2_key`
   * when present) so finalize fails if either object is missing.
   */
  async finalizeUpload(
    getToken: () => string | null,
    reportId: string,
    input: PhotoFinalizeRequest,
  ): Promise<ReportPhotoJson> {
    return await fetchJson<ReportPhotoJson>(
      `/reports/${encodeURIComponent(reportId)}/photos/finalize`,
      getToken,
      {
        method: 'POST',
        body: input,
      },
    );
  },

  /**
   * PUT a binary (original photo or thumbnail) directly to the R2
   * presigned URL the Backend returned from `requestUploadUrl`.
   *
   * IMPORTANT — this function MUST NOT carry the FieldReport Bearer
   * token. R2 presigned URLs authenticate the request via SigV4 query
   * parameters, and a stray Authorization header will cause R2 to
   * return 403. The whole point of this helper is to use a raw
   * `fetch()` that bypasses `fetchWithAuth` / `fetchJson`, both of
   * which auto-attach the bearer header to every call.
   *
   * Throws a `PhotoUploadToR2Error` (status + message) on a non-2xx
   * response so the UI can surface a retry affordance without having
   * to inspect the raw Response object.
   */
  async uploadToR2(args: {
    uploadUrl: string;
    headers: Record<string, string>;
    body: Blob;
    /** Optional override of the per-attempt timeout in ms. */
    timeoutMs?: number;
  }): Promise<void> {
    const timeoutMs = args.timeoutMs ?? 60_000;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    try {
      // Intentionally NOT using fetchWithAuth / fetchJson — those
      // inject Authorization, which would break R2 presigned PUT.
      const res = await fetch(args.uploadUrl, {
        method: 'PUT',
        // Copy headers as-is. Backend's `services/report-photos.ts`
        // returns `{ ...presign.headers, 'content-type': <mime> }` so
        // `content-type` is always present and authoritative. We
        // forward `Content-Length` when the caller knows the size
        // (Blob.size) so R2 doesn't 411 (mirrors Mobile's
        // ANDROID-PHOTO-UPLOAD-R2-411-FIX-01).
        headers: {
          ...args.headers,
          'Content-Length': String(args.body.size),
        },
        body: args.body,
        signal: controller.signal,
      });
      if (!res.ok) {
        throw new PhotoUploadToR2Error(`R2 PUT failed with status ${res.status}`, res.status);
      }
    } catch (err) {
      if (err instanceof PhotoUploadToR2Error) throw err;
      if (err instanceof Error && err.name === 'AbortError') {
        throw new PhotoUploadToR2Error(`R2 PUT timed out after ${timeoutMs}ms`, 0);
      }
      throw new PhotoUploadToR2Error(err instanceof Error ? err.message : 'R2 PUT failed', 0);
    } finally {
      window.clearTimeout(timeoutId);
    }
  },
};

/**
 * Raised by `photosApi.uploadToR2` on a non-2xx R2 response or a
 * transport-level failure. `statusCode === 0` indicates a transport
 * failure (network error, abort, timeout); other values are the
 * R2-side HTTP status. The upload controller treats both as
 * "retryable" — the Backend's idempotency on `(report_id,
 * client_photo_id)` makes a retry safe.
 */
export class PhotoUploadToR2Error extends Error {
  readonly statusCode: number;
  constructor(message: string, statusCode: number) {
    super(message);
    this.name = 'PhotoUploadToR2Error';
    this.statusCode = statusCode;
  }
}
